// src/hooks/useOrgHierarchySync.js
import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "./useApi";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const VISIBILITY_DEBOUNCE_MS = 60 * 1000; // 1 minute

/**
 * Hook that syncs org hierarchy from the backend.
 * Must be used inside AuthProvider (e.g., in PrivateRoute or below).
 *
 * - Fetches on mount (initial page load / browser refresh)
 * - Fetches periodically (every 5 minutes)
 * - Fetches on visibility change (when user returns to tab after 1+ minute)
 *
 * @returns {{ refreshOrgHierarchy: () => Promise<void> }} - Manual refresh function
 */
export const useOrgHierarchySync = () => {
  const { token, setOrgHierarchy } = useAuth();
  const apiCall = useApi();
  const lastFetchRef = useRef(0);

  const fetchHierarchy = useCallback(async () => {
    if (!token) return;
    try {
      // suppressAuthRedirect=true: a failed background hierarchy sync must not log the user out
      // / bounce through OIDC (that created an infinite re-login loop). Fail quietly here.
      const hierarchy = await apiCall("GET", "/organizations/organization-hierarchy", null, false, null, true);
      if (hierarchy) {
        setOrgHierarchy(hierarchy);
        localStorage.setItem("orgHierarchy", JSON.stringify(hierarchy));
        lastFetchRef.current = Date.now();
      }
    } catch (error) {}
  }, [token, apiCall, setOrgHierarchy]);

  useEffect(() => {
    if (!token) return;

    // Fetch immediately on mount
    fetchHierarchy();

    // Periodic refresh
    const intervalId = setInterval(fetchHierarchy, REFRESH_INTERVAL_MS);

    // Visibility change handler
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch > VISIBILITY_DEBOUNCE_MS) {
          fetchHierarchy();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, fetchHierarchy]);

  // Return refresh function for manual triggering (e.g., after org creation)
  return { refreshOrgHierarchy: fetchHierarchy };
};
